/**
 * @file    constants.ts
 * @scope   SHARED
 * @summary Cross-Program Rollup (CPR) identity, flag, aggregation and view-key constants.
 * @consumes Nothing.
 * @produces CPR_CHART_TYPE, CPR_FEATURE_FLAG, CPR_PAYLOAD_VERSION, CPR_AGGREGATION_TYPES,
 *           CPR_TREND_PERIOD_TYPES, CPR_VIEW_KEYS
 * @sideEffects None.
 * @notes   Backend contract: docs/cross-program-rollup-authoring.md.
 *
 * TODO(ai): `CPR_CHART_TYPE` is ASSUMED to be both the Guardrails dropdown `value` and the
 * persisted `Chart` value. Product said it will "probably change". When the real Guardrails rule
 * name is known, split it: keep `id`/`Chart` = 'CrossProgramRollup' (backend contract) and set
 * `optionValue` in descriptor.ts to the Guardrails value. Nothing else should need to change.
 */

/** Backend `Chart` value, descriptor `id` and (for now) dropdown `optionValue`. */
export const CPR_CHART_TYPE = 'CrossProgramRollup' as const;

/** `configDashboard` key + localStorage key (engine flag conventions, plan §12). Default off. */
export const CPR_FEATURE_FLAG = 'useDiyCrossProgramRollup';

/** Payload version written by the CPR delta. Bump + add `migrate` on shape changes. */
export const CPR_PAYLOAD_VERSION = 1;

/**
 * Max rollup metrics per widget. MVP = ONE rollup per widget.
 * WHY: `AggregationType`, `Trend` and `CustomCalculations` are widget-level and `QueryCollections`
 * are shared peers (docs/guardrails.md), so today one widget = one rollup.
 * FUTURE: multiple rollups per widget are planned. Raising this value is the single switch for the
 * UI (MetricsManager "Add" button), reducer cap and validation; the serializer (`metrics[0]`)
 * will also need the multi-rollup backend contract.
 */
export const CPR_MAX_ROLLUP_METRICS = 1;

/**
 * Backend `RollupAggregationType` values. `Unknown` is rejected by FrontStack, so it is not listed.
 * WHY no default: a wrong strategy silently produces wrong numbers (authoring guide §2).
 */
export const CPR_AGGREGATION_TYPES = [
  'Sum',
  'WeightedMean',
  'Mean',
  'AverageOfVariancesSqrt',
  'NpsRecompute',
] as const;

/** Accepted `TimePeriodType` values for the rollup trend (`Unknown` is rejected). */
export const CPR_TREND_PERIOD_TYPES = [
  'Days',
  'Weekly',
  'BiWeekly',
  'Monthly',
  'Quarterly',
  'Yearly',
  'Wave',
] as const;

/**
 * Resolves a wire value to its canonical backend enum name, case-insensitively.
 * WHY: GetData sends camelCase (`monthly`, `npsRecompute`) while the persisted contract
 * (docs/guardrails.md) uses the enum names (`Monthly`, `NpsRecompute`).
 * @returns The canonical value, or `undefined` when it is not in `allowed`.
 */
export const toCprEnum = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === 'string'
    ? allowed.find((a) => a.toLowerCase() === value.toLowerCase())
    : undefined;

export const CPR_VIEW_KEYS = {
  /** Main-tab host section (registered in DiyDrawer engineSectionRegistry). */
  rollupSection: 'cpr.rollupSection',
  metric: 'cpr.metric',
  managePrograms: 'cpr.managePrograms',
  aggregation: 'cpr.aggregation',
} as const;
